import React from "react";
import { Link } from "react-router-dom";
import { BRAND } from "@/lib/site";
import SEO from "@/components/SEO";

const LAST_UPDATED = "December 2025";

const Section = ({ n, title, children }) => (
  <section className="border-t border-zinc-200 pt-10 mt-10 first:border-t-0 first:pt-0 first:mt-0">
    <div className="tech-label text-[#C2410C]">// {n}</div>
    <h2 className="font-display text-2xl md:text-3xl font-bold uppercase tracking-tight mt-3 leading-tight">
      {title}
    </h2>
    <div className="mt-6 text-zinc-700 text-base leading-relaxed space-y-4">
      {children}
    </div>
  </section>
);

export default function Privacy() {
  return (
    <div data-testid="privacy-page">
      <SEO
        title="Privacy Policy"
        description="How Concrete Insight UK Ltd collects, uses and protects your personal information. UK GDPR compliant privacy notice."
        noindex={false}
      />

      <section className="border-b border-zinc-200">
        <div className="max-w-4xl mx-auto px-6 pt-24 pb-16 md:pt-32 md:pb-20">
          <div className="tech-label">// Legal, Privacy Policy</div>
          <h1 className="hero-heading text-4xl sm:text-5xl md:text-6xl mt-6">
            Privacy Policy
          </h1>
          <div className="mt-6 text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-500">
            Last updated: {LAST_UPDATED}
          </div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-6 py-16 md:py-24">
        <Section n="01" title="Who we are">
          <p>
            This privacy notice explains how <strong>{BRAND.name}</strong>{" "}
            (&quot;we&quot;, &quot;us&quot;, &quot;our&quot;) collects, uses
            and protects your personal information when you visit our
            website or contact us.
          </p>
          <p>
            We are the data controller for the personal information
            described in this notice. You can reach us at{" "}
            <a href={`mailto:${BRAND.email}`} className="text-[#C2410C] underline">
              {BRAND.email}
            </a>{" "}
            or at our registered office: {BRAND.address.line1}, {BRAND.address.line2}, {BRAND.address.postcode}.
          </p>
        </Section>

        <Section n="02" title="What we collect">
          <p>We collect only the personal information you give us directly, and a small amount of technical information about your visit.</p>
          <p><strong>Information you give us via the enquiry form:</strong></p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Name</li>
            <li>Company or organisation (optional)</li>
            <li>Email address</li>
            <li>Phone number (optional)</li>
            <li>Site location (optional)</li>
            <li>Project type (optional)</li>
            <li>The content of your enquiry message</li>
          </ul>
          <p><strong>Information collected automatically:</strong></p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Pages visited and how you reached them (via Google Analytics, only if you accept analytics cookies)</li>
            <li>Approximate location (country / city, derived from your IP address)</li>
            <li>Device type, browser and operating system</li>
            <li>Referring source (e.g. a Google Ads click)</li>
          </ul>
        </Section>

        <Section n="03" title="How we use it, and our lawful basis">
          <p>We use your information for the following purposes:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>To respond to your enquiry</strong> and provide the survey, diagnostic or repair services you ask about. Lawful basis: <em>legitimate interest</em> (responding to a request for information) and, once we are engaged, <em>performance of a contract</em>.</li>
            <li><strong>To keep a record of enquiries</strong> for business administration, project files and follow-up. Lawful basis: <em>legitimate interest</em>.</li>
            <li><strong>To measure how the website is used</strong> (visitors, referral sources, page performance). Lawful basis: <em>consent</em>, given via the cookie banner.</li>
            <li><strong>To measure the performance of Google Ads campaigns</strong> if you clicked one to reach our site. Lawful basis: <em>consent</em>.</li>
          </ul>
          <p>We do not use your information for automated decision-making and we do not sell it.</p>
        </Section>

        <Section n="04" title="Who sees your information">
          <p>
            Enquiries are received by the {BRAND.short} team. Beyond that, a small number of third-party service providers process data on our behalf:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Resend</strong> (resend.com) — sends the enquiry email notification to us. Data is processed in the United States under Standard Contractual Clauses.</li>
            <li><strong>MongoDB Atlas</strong> — stores enquiry records in an encrypted database.</li>
            <li><strong>Emergent</strong> (emergent.host) — hosts the website and its supporting infrastructure.</li>
            <li><strong>Google Analytics &amp; Google Ads</strong> (Alphabet Inc.) — website analytics and ad performance measurement, only where you have given consent.</li>
          </ul>
          <p>We do not share your information with any other third party unless required by law.</p>
        </Section>

        <Section n="05" title="How long we keep it">
          <p>
            <strong>Enquiries:</strong> we retain enquiry records for up to 6 years, in line with the standard limitation period under UK law for potential contractual matters.
          </p>
          <p>
            <strong>Analytics data:</strong> Google Analytics retains anonymised data for a maximum of 14 months by default.
          </p>
          <p>
            You can ask us to delete your enquiry earlier at any time (see &quot;Your rights&quot; below).
          </p>
        </Section>

        <Section n="06" title="Your rights">
          <p>Under UK GDPR you have the right to:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Ask for a copy of the personal information we hold about you</li>
            <li>Ask us to correct information that is inaccurate</li>
            <li>Ask us to delete your information (right to erasure)</li>
            <li>Ask us to restrict how we use it</li>
            <li>Object to us using it on the basis of legitimate interest</li>
            <li>Ask for a portable copy of the information you gave us</li>
            <li>Withdraw consent for analytics or ad measurement at any time</li>
          </ul>
          <p>
            To exercise any of these rights, email us at{" "}
            <a href={`mailto:${BRAND.email}`} className="text-[#C2410C] underline">
              {BRAND.email}
            </a>. We will respond within one calendar month.
          </p>
          <p>
            If you are not satisfied with how we handle your information, you can complain to the Information Commissioner&apos;s Office (ICO) at{" "}
            <a href="https://ico.org.uk/make-a-complaint/" target="_blank" rel="noopener noreferrer" className="text-[#C2410C] underline">
              ico.org.uk
            </a>.
          </p>
        </Section>

        <Section n="07" title="Cookies">
          <p>
            We use a small number of cookies. See our{" "}
            <Link to="/cookies" className="text-[#C2410C] underline">
              Cookie Policy
            </Link>{" "}
            for the full list and how to control them.
          </p>
        </Section>

        <Section n="08" title="Changes to this notice">
          <p>
            We may update this notice from time to time. If we make significant changes we will note them at the top of this page.
          </p>
        </Section>
      </div>
    </div>
  );
}
